/* eslint-disable max-lines, @typescript-eslint/no-non-null-assertion */
import * as React from 'react';
import { Button, ButtonSize, ButtonTheme } from '@cian/button-component';
import * as PropTypes from 'prop-types';
import {
  IAgentAvailabilitySchema,
  IAgentSchema,
  IKPSchema,
  IOfferSchema,
  IPhoneSchema,
  IUserSchema,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { BookingButtonWidget, BookingStatus, IBookingTracking } from '@cian/pik-booking-widget';
import { numberToPrettyString } from '@cian/utils';

import { FeedbackExperimentContainer } from '../../FeedbackExperiment/components';
import { IContext } from '../../store';
import { OfferStatus } from '../../store/offer';
import { IOfferChatsState } from '../../store/offer_chats';
import { getOfferTitle, getSquareInformation, IParent } from '../../utils/offer_helpers';
import { SimpleBotDetector } from '../../utils/simpleBotDetector';
import { PopupOwnOfferContainer } from '../popups/own_offer/container';
import { StartChatComponent } from '../start_chat_container/container';
import {
  trackAgentAvailabilityMessageSent,
  trackCallbackClick,
  trackFirstMessageClick,
  trackFirstMessageSent,
  trackFirstMessageShowPopup,
  trackGoToMessages,
  trackShowPhoneClick,
} from './tracking';

import { ABCallButton } from '../ab_call_button';
import { DynamicCalltrackingButton, EPageTypes, EPlatformTypes } from '@cian/frontend-dynamic-calltracking-component';
import { formatNumber } from '../../utils/phone';
import { CalltrackingBadgeContainer } from '../../containers/CalltrackingBadge';
import { DealRentNoticeContainer } from '../../containers/DealRentNoticeContainer';
import { ICallbackContent } from '../popups/callback/types';

import * as styles from './index.css';

export interface IOfferContactsAsideStateProps {
  abGroup: number;
  agent: IAgentSchema | null;
  isPhonesVisible: boolean;
  offer: IOfferSchema;
  offerChat: IOfferChatsState;
  user: IUserSchema | null;
  callbackContent: ICallbackContent;
  agentAvailability: IAgentAvailabilitySchema | null;
  parent: IParent | undefined;
  bookingTracking: IBookingTracking;
  bookedOfferId: number;
  isPikBookingSucceeded: boolean;
  isCommercial: boolean | null;
  kp: IKPSchema | null;
  siteBlockId: number | null;
  isAuthenticated: boolean;
  newbuildingId?: number | null;
  dynamicCalltrackingPlaceType: string;
  isDealRent: boolean;
  entryToShowEnabled: boolean;
  isPik: boolean;
  shouldRenderContactButtons: boolean;
  shouldRenderCallbackButton: boolean;
}

export interface IOfferContactsAsideDispatchProps {
  chatMessageSent(): void;
  openCallbackPopup(): void;
  setPhoneCollapse(): void;
}

type IOfferContactsAsideProps = IOfferContactsAsideStateProps & IOfferContactsAsideDispatchProps;

interface IOfferContactsAsideState {
  isChatMessageSent: boolean;
  isOwnOfferPopupOpen: boolean;
  isPhonesOpened: boolean;
  isStartChatPopupOpen: boolean;
  startChatMessage?: string;
}

const timeFormatter = (time: string) => time.slice(0, 5);

export class OfferContactsAside extends React.Component<IOfferContactsAsideProps, IOfferContactsAsideState> {
  public context: IContext;
  private botDetector = new SimpleBotDetector();

  public state: IOfferContactsAsideState = {
    isChatMessageSent: false,
    isOwnOfferPopupOpen: false,
    isPhonesOpened: false,
    isStartChatPopupOpen: false,
  };

  public static contextTypes = {
    logger: PropTypes.object,
    pikBookingContext: PropTypes.object,
  };

  public componentDidMount() {
    this.botDetector.init();
  }

  public render() {
    const {
      user,
      agent,
      isCommercial,
      offer,
      agentAvailability,
      kp,
      entryToShowEnabled,
      shouldRenderContactButtons,
      shouldRenderCallbackButton,
    } = this.props;
    const { isStartChatPopupOpen, isOwnOfferPopupOpen, startChatMessage } = this.state;

    if (offer.status !== OfferStatus.Published || entryToShowEnabled) {
      return null;
    }

    return (
      <div className={styles['container']}>
        {shouldRenderContactButtons && this.renderPhones()}
        {shouldRenderContactButtons && this.renderPrintPhones()}

        {shouldRenderCallbackButton ? (
          <div className={styles['row']}>
            {shouldRenderContactButtons && this.renderCallbackButton()}
            {this.renderPIKBookingButton()}
          </div>
        ) : (
          this.renderPIKBookingButton()
        )}

        {shouldRenderCallbackButton && this.renderWorkTimeInfo()}

        {shouldRenderContactButtons && this.renderSendMessageButton()}
        <StartChatComponent
          isOpen={isStartChatPopupOpen}
          isShowInDropdown={true}
          message={startChatMessage}
          dropdownCustomClass={!isCommercial && styles['dropdown-no-commercial']}
          onMessageChange={message => this.setState({ startChatMessage: message })}
          onMessageSent={(chatId?: string) => {
            this.props.chatMessageSent();
            if (user && agent && agentAvailability && !agentAvailability.available) {
              trackAgentAvailabilityMessageSent(offer, agent, user);
            } else {
              trackFirstMessageSent(agent, offer, user, kp && kp.id);
            }
          }}
          onClose={() => this.setState({ isStartChatPopupOpen: false })}
        />
        <PopupOwnOfferContainer
          isOpen={isOwnOfferPopupOpen}
          onClose={() => this.setState({ isOwnOfferPopupOpen: false })}
        />
      </div>
    );
  }

  private renderPIKBookingButton() {
    const { offer, parent, bookingTracking, bookedOfferId, isPikBookingSucceeded } = this.props;

    const { booking, bargainTerms } = offer;

    if (!booking || !this.context.pikBookingContext) {
      return null;
    }

    const offerDescription =
      `${getOfferTitle(offer)} · ${getSquareInformation(offer)} ` +
      `за ${numberToPrettyString(bargainTerms.prices.rur).replace(' ', '\u00a0')}\u00a0\u20bd` +
      `${parent ? ` в ЖК \u00AB${parent.name}\u00BB` : ''}`;

    const isBooked = Boolean(bookedOfferId && isPikBookingSucceeded) || booking.status === BookingStatus.inReserve;

    return (
      <BookingButtonWidget
        widgetContext={this.context.pikBookingContext}
        isBooked={isBooked}
        tracking={bookingTracking}
        bookingParams={{
          bookingPeriod: String(booking.bookingDuration),
          bookingPrice: String(booking.cost),
          cianOfferId: offer.cianId,
          offerDescription,
          pikOfferId: booking.externalId,
          similarOffersLink: booking.similarOffersUrl,
        }}
      />
    );
  }

  private renderCallbackButton() {
    const {
      agent,
      offer,
      openCallbackPopup,
      callbackContent: { buttonText },
    } = this.props;

    return (
      <Button
        theme="ghost"
        size="S"
        className={styles['button_callback']}
        onClick={() => {
          openCallbackPopup();
          trackCallbackClick(offer, agent);
        }}
      >
        {buttonText}
      </Button>
    );
  }

  private renderWorkTimeInfo() {
    const {
      offer: { workTimeInfo },
      isPik,
    } = this.props;

    if (!workTimeInfo || !workTimeInfo.callFrom || !workTimeInfo.callTo) {
      return null;
    }

    const callFrom = timeFormatter(workTimeInfo.callFrom);
    const callTo = timeFormatter(workTimeInfo.callTo);
    const baseText = isPik ? 'Принимаем звонки с' : 'Офис работает с';

    return (
      <div className={styles['callback_time']}>
        {baseText} {callFrom} до {callTo}
      </div>
    );
  }

  private renderPrintPhones() {
    const { isPhonesVisible, offer } = this.props;
    const { phones } = offer;

    if (!phones || phones.some(phone => !phone.countryCode || !phone.number)) {
      return null;
    }

    if (!isPhonesVisible) {
      return null;
    }

    return (
      <div className={styles['print_phones']}>
        {phones.map((phone, index) => (
          <a key={index} href={`tel:${phone.countryCode}${phone.number}`} className={styles['phone']}>{`${
            phone.countryCode
          } ${
            // tslint:disable-next-line:no-non-null-assertion
            phone.number!.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '$1 $2-$3-$4')
          }`}</a>
        ))}
      </div>
    );
  }

  private renderPhones() {
    const { offer, siteBlockId } = this.props;
    const { phones } = offer;

    if (!phones || phones.some(phone => !phone.countryCode || !phone.number)) {
      this.context.logger.error(`Offer #${offer.cianId}: phones is null`);

      return null;
    }

    return (
      <div className={styles['phones']}>
        {siteBlockId && this.renderDynamicCalltrackingPhone(phones[0])}
        {!siteBlockId && this.renderStaticCalltrackingPhones(phones)}
      </div>
    );
  }

  private renderStaticCalltrackingPhones = (phones: IPhoneSchema[]) => {
    if (this.state.isPhonesOpened) {
      return (
        <div>
          {phones.map((phone, index) => {
            if (phone && phone.number) {
              return (
                <a key={index} href={`tel:${phone.countryCode}${phone.number}`} className={styles['phone']}>
                  {phone.countryCode} {phone.number.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '$1 $2-$3-$4')}
                </a>
              );
            }

            return null;
          })}
          {this.props.isDealRent ? (
            <DealRentNoticeContainer />
          ) : (
            <>
              <CalltrackingBadgeContainer />
              <FeedbackExperimentContainer />
            </>
          )}
        </div>
      );
    } else {
      return (
        <Button size={ButtonSize.M} onClick={this.onShowPhoneClick}>
          <ABCallButton />
        </Button>
      );
    }
  };

  private renderDynamicCalltrackingPhone = (phone: IPhoneSchema) => {
    const { offer, siteBlockId, dynamicCalltrackingPlaceType } = this.props;
    const { isPhonesOpened } = this.state;

    return (
      <div>
        <DynamicCalltrackingButton
          onClick={this.onShowPhoneClick}
          className={styles['show-phone']}
          openPhoneClassName={styles['phone']}
          isOpen={isPhonesOpened}
          phone={formatNumber(phone) || ''}
          announcementId={offer.id}
          blockId={siteBlockId}
          placeType={dynamicCalltrackingPlaceType}
          platformType={EPlatformTypes.WebDesktop}
          pageType={EPageTypes.OfferCard}
        >
          <ABCallButton />
        </DynamicCalltrackingButton>

        {isPhonesOpened && <CalltrackingBadgeContainer />}
        {isPhonesOpened && <FeedbackExperimentContainer />}
      </div>
    );
  };

  private onShowPhoneClick = async () => {
    const { abGroup, offer, agent, offerChat, agentAvailability, user, kp } = this.props;
    const isAgentNotAvailable = agentAvailability && !agentAvailability.available;
    const { isChatsEnabled } = offerChat;

    this.setState({
      isPhonesOpened: true,
    });
    this.props.setPhoneCollapse();

    if (isChatsEnabled && isAgentNotAvailable) {
      this.onStartChatButtonClick(false);
      trackFirstMessageShowPopup(offer, agent, 'open_phone');
    }

    trackShowPhoneClick(offer, agent, user, abGroup, kp && kp.id, !this.botDetector.isHuman(), isAgentNotAvailable);
  };

  private renderSendMessageButton = () => {
    const { user, offer, agent, agentAvailability } = this.props;
    const { isChatsEnabled, chatId } = this.props.offerChat;
    const { isChatMessageSent: isChatMessageSentState, isStartChatPopupOpen } = this.state;
    const isAgentAvailable = user && agentAvailability && !agentAvailability.available;
    const isShowMessageButton = !isStartChatPopupOpen;
    const isChatMessageSent = isChatMessageSentState || !!chatId;
    const isOfferOwnedByUser = user && user.isAuthenticated && user.realtyUserId === offer.userId;

    return isChatsEnabled && isShowMessageButton ? (
      <div className={styles['button_start_chat-container']}>
        <Button
          theme={ButtonTheme.Ghost}
          onClick={() => {
            this.onStartChatButtonClick();
            if (isChatsEnabled && isAgentAvailable) {
              trackFirstMessageShowPopup(offer, agent, 'open_message');
            } else {
              trackFirstMessageShowPopup(offer, agent);
            }
          }}
        >
          {!isChatMessageSent || isOfferOwnedByUser ? 'Написать сообщение' : 'Посмотреть сообщения'}
        </Button>
      </div>
    ) : null;
  };

  private onStartChatButtonClick = (showDialogs: boolean = true) => {
    const { offer, agent, user, agentAvailability } = this.props;
    const { isChatMessageSent: isChatMessageSentState } = this.state;
    const { chatId } = this.props.offerChat;

    const isChatMessageSent = isChatMessageSentState || !!chatId;
    const isUserAuthenticated = (user && user.isAuthenticated) || false;
    const isOfferOwnedByUser = user && user.isAuthenticated && user.realtyUserId === offer.userId;
    const isAgentAvailable = agentAvailability && !agentAvailability.available;

    trackFirstMessageClick(offer, agent);

    if (isOfferOwnedByUser) {
      this.setState({ isOwnOfferPopupOpen: true });
    } else if (!isChatMessageSent && isUserAuthenticated) {
      this.setState({ isStartChatPopupOpen: true });
    } else if (isChatMessageSent && isAgentAvailable && isUserAuthenticated && !showDialogs) {
      this.setState({ isStartChatPopupOpen: true });
    } else if (!isUserAuthenticated) {
      if (window.waitForLoginPopup) {
        //tslint:disable-line:no-any
        window.waitForLoginPopup('chats'); //tslint:disable-line:no-any
      }
    } else {
      trackGoToMessages();
      window.location.href = `/dialogs/?offerId=${offer.id}`;
    }
  };
}
